import { useCallback, useEffect, useState } from "react";
import { Check, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/app/api";
import { money } from "@/app/pages/commissions/CommissionList";

const input = "w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cobalt focus:ring-2 focus:ring-cobalt/15";
const REWARD_STYLE = {
  pending: "bg-amber-50 text-amber-700", approved: "bg-indigo-50 text-indigo-700",
  payable: "bg-cobalt/10 text-cobalt", paid: "bg-emerald-50 text-emerald-700", rejected: "bg-rose-50 text-rose-700",
};
const AFFILIATE_STATUS = {
  pending_approval: ["Pending approval", "bg-amber-50 text-amber-700"],
  active: ["Active", "bg-emerald-50 text-emerald-700"],
  inactive: ["Inactive", "bg-slate-100 text-slate-500"],
  suspended: ["Suspended", "bg-rose-50 text-rose-700"],
};

export default function AffiliateDashboard() {
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [copied, setCopied] = useState("");
  const [payment, setPayment] = useState({ account_holder: "", sort_code: "", account_number: "", country: "United Kingdom" });

  const load = useCallback(() => {
    api.get("/affiliate/me").then((r) => setData(r.data)).catch((e) => {
      if (e.response?.status === 404) setMissing(true);
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const apply = async () => {
    setBusy(true);
    try {
      await api.post("/affiliate/apply", { accept_terms: true, terms_version: data.programme.terms_version });
      toast.success("Affiliate application submitted — an admin will review it.");
      setMissing(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const reaccept = async () => {
    setBusy(true);
    try {
      await api.post("/affiliate/me/accept-terms", { accept_terms: true, terms_version: data.programme.terms_version });
      toast.success("Terms accepted.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const savePayment = async () => {
    setBusy(true);
    try {
      await api.post("/affiliate/me/payment-details", payment);
      toast.success("Payment details saved securely.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copy = (text, which) => {
    navigator.clipboard?.writeText(text);
    setCopied(which);
    setTimeout(() => setCopied(""), 1500);
  };

  if (!data && !missing) return <p className="text-sm text-slatey">Loading…</p>;

  if (missing) return <ApplyFlow onApplied={() => { setMissing(false); load(); }} />;
  if (!data) return null;

  const { affiliate, programme, stats, rewards, payouts, terms_current } = data;
  const [statusLabel, statusStyle] = AFFILIATE_STATUS[affiliate.status] || [affiliate.status, "bg-slate-100 text-slatey"];
  const siteUrl = window.location.origin;
  const referralLink = `${siteUrl}/?ref=${affiliate.code}`;

  return (
    <div data-testid="affiliate-dashboard" className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-ink">Affiliate Programme</h1>
          <p className="mt-1 text-sm text-slatey">{programme.name} — share your link, earn when your referrals succeed.</p>
        </div>
        <span className={`rounded-full px-4 py-2 text-sm font-bold ${statusStyle}`} data-testid="affiliate-status">{statusLabel}</span>
      </div>

      {!terms_current && (
        <div className="rounded-3xl border border-amber-200 bg-amber-50 p-5" data-testid="terms-reaccept-banner">
          <p className="text-sm font-bold text-amber-800">The Affiliate Terms have been updated (version {programme.terms_version}). Please review and accept to continue earning.</p>
          <div className="mt-3 max-h-40 overflow-y-auto rounded-2xl bg-white p-4 text-xs leading-5 text-slatey" data-testid="terms-text">{programme.terms_text}</div>
          <button onClick={reaccept} disabled={busy} data-testid="terms-reaccept-button"
            className="mt-3 rounded-full bg-cobalt px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50">Accept version {programme.terms_version}</button>
        </div>
      )}

      {affiliate.status === "active" && (
        <section className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="affiliate-link-card">
          <h2 className="font-display text-xl font-bold text-ink">Your referral link</h2>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <code className="rounded-2xl bg-surface px-4 py-3 text-sm font-bold text-cobalt" data-testid="affiliate-code">{affiliate.code}</code>
            <code className="min-w-0 flex-1 truncate rounded-2xl bg-surface px-4 py-3 text-sm text-ink" data-testid="affiliate-link">{referralLink}</code>
            <button onClick={() => copy(referralLink, "link")} data-testid="copy-link-button"
              className="inline-flex items-center gap-2 rounded-full bg-cobalt px-4 py-2.5 text-xs font-bold text-white">
              {copied === "link" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied === "link" ? "Copied" : "Copy link"}
            </button>
          </div>
        </section>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5" data-testid="affiliate-stats">
        {[["Visitors", stats.visitors], ["Registered", stats.attributed], ["Journeys", stats.journeys],
          ["Applications", stats.applications], ["Qualified", stats.qualified]].map(([label, value]) => (
          <div key={label} className="rounded-3xl border border-slate-200 bg-white p-4" data-testid={`stat-${label.toLowerCase()}`}>
            <p className="text-xs font-bold uppercase text-slatey">{label}</p>
            <p className="mt-1 font-display text-2xl font-bold text-ink">{value}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="affiliate-rewards">
          <h2 className="font-display text-xl font-bold text-ink">Rewards</h2>
          <div className="mt-3 grid grid-cols-2 gap-2 text-center sm:grid-cols-4">
            {[["Pending", "pending"], ["Approved", "approved"], ["Payable", "payable"], ["Paid", "paid"]].map(([label, key]) => (
              <div key={key} className="rounded-2xl bg-surface p-3" data-testid={`reward-total-${key}`}>
                <p className="text-[11px] font-bold uppercase text-slatey">{label}</p>
                <p className="mt-0.5 font-bold text-ink">{money(stats.reward_totals[key], programme.currency)}</p>
              </div>
            ))}
          </div>
          <p className="mt-3 text-sm font-bold text-ink" data-testid="total-earnings">Total earnings: {money(stats.total_earnings, programme.currency)}</p>
          <ul className="mt-3 space-y-2">
            {rewards.map((reward, index) => (
              <li key={reward.id || index} className="flex items-center justify-between rounded-2xl bg-surface px-4 py-3 text-sm" data-testid={`reward-${reward.id}`}>
                <span className="text-slatey">{reward.qualifying_event.replaceAll("_", " ")} · {reward.created_at?.slice(0, 10)}</span>
                <span className="flex items-center gap-2">
                  <span className="font-bold text-ink">{money(reward.amount, reward.currency)}</span>
                  <span className={`rounded-full px-2.5 py-1 text-xs font-bold capitalize ${REWARD_STYLE[reward.status]}`} data-testid={`reward-status-${reward.id}`}>{reward.status}</span>
                </span>
              </li>
            ))}
            {!rewards.length && <li className="text-sm text-slatey">No rewards yet — they appear when your referrals succeed.</li>}
          </ul>
        </section>

        <div className="space-y-5">
          <section className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="payment-details-card">
            <h2 className="font-display text-xl font-bold text-ink">Payment details</h2>
            <p className="mt-1 text-xs text-slatey">Used only for manual payouts. Stored securely; shown masked after saving.</p>
            {affiliate.payment_details && (
              <p className="mt-2 rounded-2xl bg-surface p-3 text-xs text-slatey" data-testid="payment-details-masked">
                Saved: {affiliate.payment_details.account_holder} · sort code {affiliate.payment_details.sort_code} · account {affiliate.payment_details.account_number} · {affiliate.payment_details.country}
              </p>
            )}
            <div className="mt-3 space-y-2">
              <input value={payment.account_holder} onChange={(e) => setPayment((p) => ({ ...p, account_holder: e.target.value }))} className={input} placeholder="Account holder name" data-testid="payment-account-holder" />
              <div className="grid grid-cols-2 gap-2">
                <input value={payment.sort_code} onChange={(e) => setPayment((p) => ({ ...p, sort_code: e.target.value }))} className={input} placeholder="Sort code" data-testid="payment-sort-code" />
                <input value={payment.account_number} onChange={(e) => setPayment((p) => ({ ...p, account_number: e.target.value }))} className={input} placeholder="Account number" data-testid="payment-account-number" />
              </div>
              <input value={payment.country} onChange={(e) => setPayment((p) => ({ ...p, country: e.target.value }))} className={input} placeholder="Country" data-testid="payment-country" />
              <button onClick={savePayment} disabled={busy || !payment.account_holder || !payment.sort_code || !payment.account_number}
                data-testid="payment-save" className="w-full rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white disabled:opacity-50">
                {busy ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : "Save payment details"}
              </button>
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="payment-history">
            <h2 className="font-display text-xl font-bold text-ink">Payment history</h2>
            <ul className="mt-3 space-y-2">
              {payouts.map((payout, index) => (
                <li key={payout.id || index} className="rounded-2xl bg-surface px-4 py-3 text-sm" data-testid={`payout-${payout.id}`}>
                  <div className="flex justify-between"><span className="font-bold text-ink">{money(payout.amount, payout.currency)}</span>
                    <span className="text-xs text-slatey">{payout.paid_at?.slice(0, 10)}</span></div>
                  <p className="mt-0.5 text-xs text-slatey">Reference: {payout.reference}{payout.note ? ` · ${payout.note}` : ""}</p>
                </li>
              ))}
              {!payouts.length && <li className="text-sm text-slatey">No payments yet.</li>}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}

/** Public-facing apply flow: programme terms + apply (pending admin approval). */
function ApplyFlow({ onApplied }) {
  const [programme, setProgramme] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/affiliate/programme-summary").then((r) => setProgramme(r.data)).catch(() => setProgramme(null));
  }, []);

  const apply = async () => {
    setBusy(true);
    try {
      await api.post("/affiliate/apply", { accept_terms: true, terms_version: programme.terms_version });
      toast.success("Affiliate application submitted — an admin will review it.");
      onApplied();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="affiliate-apply" className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-ink">Become an Affiliate</h1>
        <p className="mt-1 text-sm text-slatey">Refer prospective learners with your unique link and earn a reward when the configured qualifying event occurs. Your existing OpenDegree account is used — no separate login. Admin approval is required.</p>
      </div>
      {programme && (
        <section className="rounded-3xl border border-slate-200 bg-white p-5">
          <h2 className="font-display text-xl font-bold text-ink">Affiliate Terms &amp; Conditions <span className="text-xs font-bold text-slatey">(v{programme.terms_version})</span></h2>
          <div className="mt-3 max-h-56 overflow-y-auto rounded-2xl bg-surface p-4 text-sm leading-6 text-slatey" data-testid="apply-terms-text">{programme.terms_text}</div>
          <label className="mt-4 flex items-start gap-3 text-sm font-semibold text-ink">
            <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-1 h-4 w-4 accent-cobalt" data-testid="apply-accept-terms" />
            I have read and accept the Affiliate Terms &amp; Conditions (version {programme.terms_version})
          </label>
          <button onClick={apply} disabled={!accepted || busy} data-testid="apply-submit"
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Apply to become an affiliate
          </button>
        </section>
      )}
    </div>
  );
}
